import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../../components/DashboardSidebar';
import { Menu } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import 'react-toastify/dist/ReactToastify.css';


export default function SubscriptionPage() {
  const auth = useContext(AuthContext);
  const token = auth?.user?.token;
  const navigate = useNavigate();
  const location = useLocation();
  const [currency, setCurrency] = useState('USD'); // Default fallback
  const [isCancelling, setIsCancelling] = useState(false);
const [isSwitching, setIsSwitching] = useState(''); // store planId that is switching


  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lang, setLang] = useState(auth?.user?.language || 'en');
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 🆕 added billing cycle toggle
  const getCurrencySymbol = (code) => {
    const symbols = {
      USD: '$',
      INR: '₹',
      AED: 'د.إ',
      EUR: '€',
      GBP: '£',
      SAR: '﷼'
    };
    return symbols[code?.toUpperCase()] || code;
  };
  
  useEffect(() => {
    const loadData = async () => {
      try {
        const userRes = await fetchWithAuth(`${process.env.REACT_APP_API_BASE}/api/users/me`);
        const userData = await userRes.json();
        setCurrency(userData.currency || 'USD');
        const plansRes = await fetchWithAuth(`${process.env.REACT_APP_API_BASE}/api/plans`);
        const allPlans = await plansRes.json();
        setPlans(allPlans);

        let active = allPlans.find(p => p._id === userData.planId);
        if (!active && userData.planId) {
          const detailRes = await fetchWithAuth(`${process.env.REACT_APP_API_BASE}/api/plans/${userData.planId}`);
          if (detailRes.ok) active = await detailRes.json();
        }

        setCurrentPlan(active || null);
      } catch (err) {
        console.error('Error loading subscription data:', err);
        toast.error('Failed to load subscription info');
      }
    };

    if (token) loadData();
  }, [token]);

  async function handleCancelSubscription() {
    const confirmCancel = window.confirm('Are you sure you want to cancel your subscription and move to Free Plan?');
    if (!confirmCancel) return;
  
    setIsCancelling(true); // 🆕
  
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_API_BASE}/api/users/cancel-subscription`, {
        method: 'POST'
      });
  
      if (!res.ok) {
        const { message } = await res.json();
        toast.error(message || 'Cancellation failed');
      } else {
        toast.success('Subscription cancelled. Now on Free Plan');
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (err) {
      console.error('Cancel subscription error:', err);
      toast.error('Server error');
    } finally {
      setIsCancelling(false); // 🆕
    }
  }
  

  async function handleSwitchPlan(planId) {
    setIsSwitching(planId); // 🆕
  
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_API_BASE}/api/payment/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          planId,
          billingCycle,
          source: 'subscription' // ✅
        })
      });
  
      const data = await res.json();
  
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.message || 'Failed to start checkout session');
      }
    } catch (err) {
      console.error('Switch plan error:', err);
      toast.error('Server error');
    } finally {
      setIsSwitching(''); // 🆕
    }
  }
  

 
  

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ToastContainer position="top-right" />
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} fixed md:relative md:block z-40 w-64`}>
        <Sidebar
          currentLang={lang}
          activeSection="account"
          onLangChange={setLang}
          onCloseMobile={() => setSidebarOpen(false)}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        {/* Mobile Hamburger */}
        <button onClick={() => setSidebarOpen(true)} className="block md:hidden mb-4">
          <Menu className="w-6 h-6 text-gray-700" />
        </button>

        <h2 className="text-3xl font-semibold mb-6 text-gray-800">Subscription & Plan</h2>

        {/* Current Plan Overview */}
        {currentPlan ? (
          <div className="bg-white p-6 rounded shadow mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Your Current Plan: {currentPlan.name}
            </h3>
            <ul className="list-disc ml-5 text-sm text-gray-700 space-y-1">
              <li>Requests: {currentPlan.requestLimit} / month</li>
              <li>Tokens: {currentPlan.tokenLimit} / month</li>
              <li>Apps: {currentPlan.apps.includes('all') ? 'All Apps' : `${currentPlan.apps.length} Apps`}</li>
              <li>Priority Support: {currentPlan.prioritySupport ? 'Yes' : 'No'}</li>
              <li>Multi-model Access: {currentPlan.allowedModels.length > 1 ? 'Yes' : 'No'}</li>
            </ul>
          </div>
        ) : (
          <p className="text-sm text-gray-500 mb-6">
            You are not subscribed to any plan yet.
          </p>
        )}

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-6">
          <button
            className={`px-4 py-2 border rounded-l ${billingCycle === 'monthly' ? 'bg-blue-600 text-white' : 'bg-white text-black'}`}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button
            className={`px-4 py-2 border rounded-r ${billingCycle === 'yearly' ? 'bg-blue-600 text-white' : 'bg-white text-black'}`}
            onClick={() => setBillingCycle('yearly')}
          >
            Yearly (Save more)
          </button>
        </div>

        {/* Available Plans */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {plans
  .filter(plan => {
    if (!plan.isActive) return false;
    if (!currentPlan) return true; // if no current plan, show all
    const isCurrentPlanPaid = (currentPlan.monthlyPrice || currentPlan.yearlyPrice) > 0;
    const isPlanFree = (plan.monthlyPrice || plan.yearlyPrice) === 0;
    if (isCurrentPlanPaid && isPlanFree) return false; // ✅ hide free plans if current plan is paid
    return true;
  })
  .map(plan => (
    <div
      key={plan._id}
      className={`border rounded-lg p-6 bg-white shadow hover:shadow-md transition-all duration-200 ${
        plan._id === currentPlan?._id ? 'ring-2 ring-blue-500' : 'border-gray-200'
      }`}
    >
        <h4 className="text-lg font-bold text-gray-800 mb-2">
          {plan.name}
        </h4>
        <p className="text-gray-600 text-sm mb-2 capitalize">
          {billingCycle} billing
        </p>
        <p className="text-blue-600 font-semibold text-lg mb-4">
  {billingCycle === 'yearly' ? (
    plan.yearlyPrice > 0 ? (
      <>
        <span className="line-through text-sm text-gray-500 mr-1">
          {getCurrencySymbol(currency)}
          {(plan.monthlyPrice * 12).toLocaleString()}
        </span>
        {getCurrencySymbol(currency)}
        {plan.yearlyPrice.toLocaleString()} / Year
      </>
    ) : (
      'Free'
    )
  ) : (
    plan.monthlyPrice > 0
      ? `${getCurrencySymbol(currency)}${plan.monthlyPrice.toLocaleString()} / Month`
      : 'Free'
  )}
</p>

        <ul className="text-sm text-gray-700 space-y-1 mb-4">
          <li>Requests: {plan.requestLimit} / month</li>
          <li>Tokens: {plan.tokenLimit} / month</li>
          <li>Apps: {plan.apps.includes('all') ? 'All Apps' : `${plan.apps.length} Apps`}</li>
          <li>Priority Support: {plan.prioritySupport ? 'Yes' : 'No'}</li>
          <li>Multi-model Access: {plan.allowedModels.length > 1 ? 'Yes' : 'No'}</li>
        </ul>
        {plan._id !== currentPlan?._id ? (
         <button
         className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 text-sm"
         onClick={() => handleSwitchPlan(plan._id)}
         disabled={isSwitching === plan._id} // 🆕
       >
         {isSwitching === plan._id ? 'Processing...' : 'Switch to this Plan'}
       </button>
       
        ) : (
          <p className="text-green-600 text-sm font-semibold text-center">
            Current Plan
          </p>
        )}
      </div>
    ))}
</div>

{/* Cancel Subscription */}
<div className="mt-10">
  <h3 className="text-xl font-semibold text-gray-800 mb-4">
    Cancel your subscription
  </h3>

  <div className="bg-white p-6 rounded shadow">
  {currentPlan && (currentPlan.monthlyPrice > 0 || currentPlan.yearlyPrice > 0) && (
          <div className="mb-8">
            <button
  onClick={handleCancelSubscription}
  disabled={isCancelling} // 🆕
  className="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 text-sm"
>
  {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
</button>
          </div>
        )}
  </div>
</div>




        
        
      </div>
    </div>
  );
}
